/**
 * TemplateRowMenu — ⋯ på hver rad i malbiblioteket (#159, steg 9b, Stell_Maler).
 *
 * Rediger, Dupliser og Arkiver (eller Gjenopprett i arkivet) sto før som tre
 * knapper på hver rad, og på telefonen tok de plassen fra tittelen. Skissen har
 * én ⋯-knapp; valgene ligger bak den. Samme mønster som AssistantMenu: en
 * utfoldbar knapp (`aria-expanded`) med vanlige knapper i panelet, ikke en
 * ARIA-meny. Valget lukker panelet og setter fokus på ⋯ før det kjøres, så et
 * skjema eller en dialog som åpnes har et sted å gi fokus tilbake til.
 *
 * Test-id-ene til de tre handlingene er de samme som da de var knapper på raden.
 */
import { useId, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { useMenuDismiss } from '../../lib/useMenuDismiss';

const ITEM =
  'flex min-h-11 w-full items-center rounded-sm px-3 text-left text-sm font-bold hover:bg-inset';

interface TemplateRowMenuProps {
  id: string;
  title: string;
  archived: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
}

export function TemplateRowMenu({
  id,
  title,
  archived,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
}: TemplateRowMenuProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const onBlur = useMenuDismiss(open, setOpen, rootRef, triggerRef);

  function pick(action: () => void) {
    triggerRef.current?.focus();
    setOpen(false);
    action();
  }

  return (
    <div ref={rootRef} className="relative shrink-0" onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`chore-template-menu-${id}`}
        aria-label={t('admin.choreTemplates.menuLabel', { title })}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-inset"
      >
        <Icon.More size={20} />
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute right-0 top-full z-10 mt-1 flex w-52 flex-col gap-0.5 rounded-md border border-card-line bg-sheet p-1.5 shadow-md"
        >
          <button
            type="button"
            data-testid={`chore-template-edit-${id}`}
            onClick={() => pick(onEdit)}
            className={`${ITEM} text-ink`}
          >
            {t('admin.choreTemplates.edit')}
          </button>
          <button
            type="button"
            data-testid={`chore-template-duplicate-${id}`}
            onClick={() => pick(onDuplicate)}
            className={`${ITEM} text-ink`}
          >
            {t('admin.choreTemplates.duplicate')}
          </button>
          {archived ? (
            <button
              type="button"
              data-testid={`chore-template-restore-${id}`}
              onClick={() => pick(onRestore)}
              className={`${ITEM} text-ink`}
            >
              {t('admin.choreTemplates.restore')}
            </button>
          ) : (
            <button
              type="button"
              data-testid={`chore-template-archive-${id}`}
              onClick={() => pick(onArchive)}
              className={`${ITEM} text-bad`}
            >
              {t('admin.choreTemplates.archive')}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
